/**
 * Heyoravix Image Slider
 * Self-contained Web Component. No external libraries.
 *
 * Progressive enhancement: the carousel is a plain scrollable <ul> with
 * CSS scroll-snap by default (works with zero JS). This script only adds
 * arrows, dynamic pagination (recalculated via ResizeObserver), keyboard
 * navigation and autoplay.
 */

(function () {
  if (customElements.get('heyoravix-image-slider')) return;

  class HeyoravixImageSlider extends HTMLElement {
    constructor() {
      super();
      this._onResize = this._onResize.bind(this);
      this._onTrackScroll = this._onTrackScroll.bind(this);
      this._onKeydown = this._onKeydown.bind(this);
      this._onVisibilityChange = this._onVisibilityChange.bind(this);
      this._onIntersect = this._onIntersect.bind(this);
      this._onBlockSelect = this._onBlockSelect.bind(this);
      this._onBlockDeselect = this._onBlockDeselect.bind(this);
      this._scrollRaf = null;
      this._currentPage = 0;
      this._pages = [];
      this._inViewport = true;
      this._pausedByEditor = false;
      this._pausedByHover = false;
      this._pausedByFocus = false;
      this._pausedByInteraction = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId;
      this.loop = this.dataset.loop === 'true';
      this.autoplayEnabled = this.dataset.autoplay === 'true';
      this.autoplayInterval = (parseFloat(this.dataset.autoplayInterval) || 5) * 1000;
      this.autoplayDesktop = this.dataset.autoplayDesktop === 'true';
      this.autoplayMobile = this.dataset.autoplayMobile === 'true';
      this.pauseOnHover = this.dataset.pauseOnHover === 'true';
      this.pauseOnFocus = this.dataset.pauseOnFocus === 'true';
      this.stopAfterInteraction = this.dataset.stopAfterInteraction === 'true';
      this.breakpoint = parseInt(this.dataset.breakpoint, 10) || 750;

      this.$track = this.querySelector('[data-hv-track]');
      this.$prev = this.querySelector('[data-hv-prev]');
      this.$next = this.querySelector('[data-hv-next]');
      this.$pagination = this.querySelector('[data-hv-pagination]');
      this.$slides = Array.from(this.querySelectorAll('[data-hv-slide]'));

      if (!this.$track || this.$slides.length === 0) return;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      this._bindArrows();
      this._bindPaginationDelegate();
      this.$track.addEventListener('scroll', this._onTrackScroll, { passive: true });
      this.$track.addEventListener('keydown', this._onKeydown);

      this._resizeObserver = new ResizeObserver(() => this._onResize());
      this._resizeObserver.observe(this.$track);

      this._intersectionObserver = new IntersectionObserver(this._onIntersect, { threshold: 0.1 });
      this._intersectionObserver.observe(this);

      if (this.pauseOnHover) {
        this.addEventListener('mouseenter', () => {
          this._pausedByHover = true;
        });
        this.addEventListener('mouseleave', () => {
          this._pausedByHover = false;
        });
      }

      if (this.pauseOnFocus) {
        this.addEventListener('focusin', () => {
          this._pausedByFocus = true;
        });
        this.addEventListener('focusout', () => {
          this._pausedByFocus = false;
        });
      }

      document.addEventListener('visibilitychange', this._onVisibilityChange);
      document.addEventListener('shopify:block:select', this._onBlockSelect);
      document.addEventListener('shopify:block:deselect', this._onBlockDeselect);

      this._recalculate();
      this._startAutoplayIfNeeded();
    }

    disconnectedCallback() {
      this._teardown();
    }

    _teardown() {
      if (this._resizeObserver) this._resizeObserver.disconnect();
      if (this._intersectionObserver) this._intersectionObserver.disconnect();
      if (this.$track) {
        this.$track.removeEventListener('scroll', this._onTrackScroll);
        this.$track.removeEventListener('keydown', this._onKeydown);
      }
      document.removeEventListener('visibilitychange', this._onVisibilityChange);
      document.removeEventListener('shopify:block:select', this._onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this._onBlockDeselect);
      this._stopAutoplay();
      if (this._scrollRaf) cancelAnimationFrame(this._scrollRaf);
    }

    /* ---------------- Page calculation ---------------- */

    _recalculate() {
      this._buildPages();
      this._buildPagination();
      this._updateArrowState();
      this._updateActivePageFromScroll();
    }

    _buildPages() {
      const trackWidth = this.$track.clientWidth;
      const slideLefts = this.$slides.map((el) => el.offsetLeft);
      const trackScrollWidth = this.$track.scrollWidth;

      // A "page" starts at every slide whose left edge, if scrolled to, would
      // still allow the remaining slides to fit — i.e. a real distinct snap
      // position. This naturally collapses multiple visible cards into fewer
      // pages on desktop and expands to one-per-slide on narrow viewports.
      const pages = [];
      let lastPageStart = -Infinity;

      slideLefts.forEach((left) => {
        const maxScrollForThisPage = Math.min(left, trackScrollWidth - trackWidth);
        if (maxScrollForThisPage - lastPageStart > 4 || pages.length === 0) {
          pages.push(Math.max(0, maxScrollForThisPage));
          lastPageStart = maxScrollForThisPage;
        }
      });

      // Ensure the last page is reachable even with rounding.
      const finalScroll = trackScrollWidth - trackWidth;
      if (finalScroll > 0 && pages[pages.length - 1] < finalScroll - 4) {
        pages.push(finalScroll);
      }

      this._pages = pages.length ? pages : [0];

      const fitsEntirely = trackScrollWidth <= trackWidth + 4;
      this._singlePage = fitsEntirely || this._pages.length <= 1;
    }

    _buildPagination() {
      if (!this.$pagination) return;
      this.$pagination.innerHTML = '';

      if (this._singlePage || this.$slides.length <= 1) {
        this.$pagination.hidden = true;
        if (this.$prev) this.$prev.hidden = true;
        if (this.$next) this.$next.hidden = true;
        return;
      }

      const isMobile = window.innerWidth <= this.breakpoint;
      const showPagination = isMobile
        ? this.dataset.showPaginationMobile !== 'false'
        : this.dataset.showPaginationDesktop !== 'false';
      const showArrows = isMobile
        ? this.dataset.showArrowsMobile === 'true'
        : this.dataset.showArrowsDesktop !== 'false';

      this.$pagination.hidden = !showPagination;
      if (this.$prev) this.$prev.hidden = !showArrows;
      if (this.$next) this.$next.hidden = !showArrows;

      this._pages.forEach((scrollLeft, index) => {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hv-is-dot';
        dot.setAttribute('role', 'tab');
        dot.setAttribute('aria-label', 'Go to page ' + (index + 1) + ' of ' + this._pages.length);
        dot.dataset.hvPageIndex = String(index);
        dot.addEventListener('click', () => this._goToPage(index, true));
        this.$pagination.appendChild(dot);
      });

      this._syncActiveDot();
    }

    /* ---------------- Scroll tracking ---------------- */

    _onTrackScroll() {
      if (this._scrollRaf) return;
      this._scrollRaf = requestAnimationFrame(() => {
        this._scrollRaf = null;
        this._updateActivePageFromScroll();
        this._updateArrowState();
      });
    }

    _updateActivePageFromScroll() {
      const scrollLeft = this.$track.scrollLeft;
      let closest = 0;
      let closestDist = Infinity;
      this._pages.forEach((pageScroll, index) => {
        const dist = Math.abs(pageScroll - scrollLeft);
        if (dist < closestDist) {
          closestDist = dist;
          closest = index;
        }
      });
      if (closest !== this._currentPage) {
        this._currentPage = closest;
        this._syncActiveDot();
      }
    }

    _syncActiveDot() {
      if (!this.$pagination) return;
      Array.from(this.$pagination.children).forEach((dot, index) => {
        const isActive = index === this._currentPage;
        dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      });
    }

    /* ---------------- Navigation ---------------- */

    _goToPage(index, userInitiated) {
      if (!this._pages.length) return;
      let target = index;
      if (target < 0) target = this.loop ? this._pages.length - 1 : 0;
      if (target >= this._pages.length) target = this.loop ? 0 : this._pages.length - 1;

      this._currentPage = target;
      this.$track.scrollTo({
        left: this._pages[target],
        behavior: this.reducedMotion ? 'auto' : 'smooth',
      });
      this._syncActiveDot();

      if (userInitiated) {
        this._registerInteraction();
      }
    }

    _bindArrows() {
      if (this.$prev) {
        this.$prev.addEventListener('click', () => this._goToPage(this._currentPage - 1, true));
      }
      if (this.$next) {
        this.$next.addEventListener('click', () => this._goToPage(this._currentPage + 1, true));
      }
    }

    _bindPaginationDelegate() {
      // Dots themselves already have click handlers attached at build time.
    }

    _updateArrowState() {
      if (!this.$prev || !this.$next) return;
      if (this.loop) {
        this.$prev.disabled = false;
        this.$next.disabled = false;
        return;
      }
      this.$prev.disabled = this._currentPage <= 0;
      this.$next.disabled = this._currentPage >= this._pages.length - 1;
    }

    _onKeydown(event) {
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        this._goToPage(this._currentPage + 1, true);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        this._goToPage(this._currentPage - 1, true);
      }
    }

    _onResize() {
      const wasHidden = this.$pagination ? this.$pagination.hidden : true;
      this._buildPages();
      this._buildPagination();
      this._updateArrowState();
    }

    _registerInteraction() {
      if (this.stopAfterInteraction) {
        this._stopAutoplay();
      } else {
        this._restartAutoplayTimer();
      }
    }

    /* ---------------- Autoplay ---------------- */

    _startAutoplayIfNeeded() {
      if (!this.autoplayEnabled || this.reducedMotion) return;
      const isMobile = window.innerWidth <= this.breakpoint;
      if (isMobile && !this.autoplayMobile) return;
      if (!isMobile && !this.autoplayDesktop) return;
      this._restartAutoplayTimer();
    }

    _restartAutoplayTimer() {
      this._stopAutoplay();
      if (!this.autoplayEnabled || this.reducedMotion) return;
      this._autoplayTimer = setInterval(() => {
        if (this._shouldPauseAutoplay()) return;
        this._goToPage(this._currentPage + 1, false);
      }, this.autoplayInterval);
    }

    _stopAutoplay() {
      if (this._autoplayTimer) {
        clearInterval(this._autoplayTimer);
        this._autoplayTimer = null;
      }
    }

    _shouldPauseAutoplay() {
      if (this._singlePage) return true;
      if (!this._inViewport) return true;
      if (document.hidden) return true;
      if (this._pausedByHover) return true;
      if (this._pausedByFocus) return true;
      if (this._pausedByEditor) return true;
      if (!this.loop && this._currentPage >= this._pages.length - 1) {
        // At the end with no loop: stop naturally instead of jumping back.
        return true;
      }
      return false;
    }

    _onIntersect(entries) {
      const entry = entries[0];
      if (!entry) return;
      this._inViewport = entry.isIntersecting;
    }

    _onVisibilityChange() {
      // Handled reactively inside _shouldPauseAutoplay via document.hidden.
    }

    _onBlockSelect(event) {
      const blockId = event.detail && event.detail.blockId;
      if (!blockId) return;
      const slide = this.querySelector('[data-hv-slide][data-block-id="' + blockId + '"]') ||
        Array.from(this.$slides).find((el) => el.getAttribute('data-block-id') === blockId);
      // Fall back: match via Shopify's standard block selection attribute if present.
      const target = slide || this.querySelector('[data-shopify-editor-block="' + blockId + '"]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      }
      this._pausedByEditor = true;
    }

    _onBlockDeselect() {
      this._pausedByEditor = false;
    }
  }

  customElements.define('heyoravix-image-slider', HeyoravixImageSlider);

  document.addEventListener('shopify:section:unload', (event) => {
    const el = event.target && event.target.querySelector
      ? event.target.querySelector('heyoravix-image-slider')
      : null;
    if (el && typeof el._teardown === 'function') {
      el._teardown();
    }
  });
})();